import Vue from 'vue';
import VueCompositionApi, { Ref, reactive, computed } from '@vue/composition-api';

import { Stateful, Immutable } from './common';

Vue.use(VueCompositionApi);

export type QuizState = {
  version: number;
  currentQuestion: number;

  responses: number[];
};

export type QuizOption = {
  text: string;
  correct?: boolean;
};

export type QuizQuestion = {
  text: string;
  responses: QuizOption[];
};

export type Quiz = QuizQuestion[];

export type QuizModel = Stateful<QuizState> & {
  type: 'QuizModel';
  selectedOption: Immutable<Ref<number | undefined>>;
  score: Immutable<Ref<number>>;
  setup(quiz: Quiz): void;
  restart(): void;
  prev(): void;
  next(): void;
  select(index: number): void;
};

export const DefaultQuizState: QuizState = {
  version: Date.now(),
  currentQuestion: 0,

  responses: [],
};

export function createQuizModel(state = DefaultQuizState): QuizModel {
  // deep copy trick with json serialization
  const _state: QuizState = reactive(JSON.parse(JSON.stringify(state)));

  let _quiz: Quiz = [];

  const _selectedOption = () => computed(() => _state.responses[_state.currentQuestion]);

  const score = computed(() => {
    let _score = 0;
    for (let i = 0; i < _state.responses.length; i++) {
      const responseIndex = _state.responses[i];

      if (_quiz[i].responses[responseIndex] !== undefined && _quiz[i].responses[responseIndex].correct) {
        _score += 1;
      }
    }

    return _score;
  });

  function _invalidate() {
    _state.version = Date.now();
  }

  function restart() {
    _state.currentQuestion = 0;
    _state.responses = Array(_quiz.length).fill(-1);

    _invalidate();
  }

  function select(index: number) {
    _state.responses[_state.currentQuestion] = index;

    _invalidate();
  }

  function setup(quiz: Quiz) {
    _quiz = quiz;

    restart();
  }

  function prev() {
    if (_state.currentQuestion > 0) _state.currentQuestion -= 1;
    _invalidate();
  }

  function next() {
    if (_state.currentQuestion < _quiz.length) _state.currentQuestion += 1;
    _invalidate();
  }

  return {
    type: 'QuizModel',
    state: _state,
    score,
    // getter is workaround since _state.responses is not reactive if it isn't initialized yet
    get selectedOption() {
      return _selectedOption();
    },
    restart,
    select,
    setup,
    prev,
    next,
  };
}
